import { useState } from "react";
import { useSnackbar } from "notistack";
import { useFunctionLogs } from "../hooks/useFunctionLogs";
import { useIcps } from "../hooks/useIcps";
import { useRescore } from "../hooks/useMutations";
import { errorPayload, statusFromMutation } from "../lib/mutationStatus";
import { CampaignInput } from "./CampaignInput";
import { LogStream } from "./LogStream";
import { ContactResearchResult } from "./results/ContactResearchResult";
import { JsonResult } from "./results/JsonResult";
import { StatusLine } from "./StatusLine";

interface Props {
  selectedIds: Set<string>;
  filteredCount: number;
  onSelectAllFiltered: () => void;
  onClear: () => void;
}

export function SelectionRescorePanel({
  selectedIds,
  filteredCount,
  onSelectAllFiltered,
  onClear,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: icps } = useIcps();
  const mutation = useRescore();
  const [runStartTime, setRunStartTime] = useState<Date | null>(null);
  const logs = useFunctionLogs(
    "cf_rescore_lead",
    runStartTime,
    mutation.isPending,
  );

  const [icpName, setIcpName] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [ignoreOtherCampaigns, setIgnoreOtherCampaigns] = useState(false);
  const [ignorePipedriveDedup, setIgnorePipedriveDedup] = useState(true);

  const selectedIcp = icpName || icps?.[0]?.name || "";
  const count = selectedIds.size;

  const onRun = () => {
    if (count === 0) {
      enqueueSnackbar("No contacts selected.", { variant: "warning" });
      return;
    }
    if (count > 1000) {
      enqueueSnackbar(
        `Up to 1000 contacts can be rescored at once (selected ${count}).`,
        { variant: "warning" },
      );
      return;
    }
    if (!selectedIcp) {
      enqueueSnackbar("ICP name is required.", { variant: "warning" });
      return;
    }
    if (!campaignId.startsWith("cam_")) {
      enqueueSnackbar("Campaign ID must start with cam_.", {
        variant: "warning",
      });
      return;
    }
    logs.reset();
    setRunStartTime(new Date());
    mutation.mutate({
      icp_name: selectedIcp,
      campaign_id: campaignId,
      dry_run: dryRun,
      ignore_other_campaigns: ignoreOtherCampaigns,
      ignore_pipedrive_dedup: ignorePipedriveDedup,
      apollo_ids: Array.from(selectedIds),
    });
  };

  const { variant, message } = statusFromMutation(mutation);

  return (
    <div className="selection-panel">
      <div className="selection-bar">
        <strong>{count} selected</strong>
        <button
          className="secondary"
          onClick={onSelectAllFiltered}
          disabled={filteredCount === 0 || count >= filteredCount}
        >
          Select all filtered ({filteredCount})
        </button>
        <button
          className="secondary"
          onClick={onClear}
          disabled={count === 0}
        >
          Clear
        </button>
      </div>

      {count > 0 && (
        <>
          <div className="field-row" style={{ marginTop: 12 }}>
            <div className="field">
              <label htmlFor="sel-rs-icp">ICP name</label>
              <select
                id="sel-rs-icp"
                value={selectedIcp}
                onChange={(e) => {
                  setIcpName(e.target.value);
                  setCampaignId("");
                }}
              >
                {(icps ?? []).map((icp) => (
                  <option key={icp.name} value={icp.name}>
                    {icp.display_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="sel-rs-campaign">Campaign ID</label>
              <CampaignInput
                id="sel-rs-campaign"
                value={campaignId}
                onChange={setCampaignId}
                contextIcp={selectedIcp}
              />
            </div>
          </div>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={ignoreOtherCampaigns}
              onChange={(e) => setIgnoreOtherCampaigns(e.target.checked)}
            />
            Ignore other Lemlist campaigns (allow contacts to be in multiple
            campaigns at once)
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={ignorePipedriveDedup}
              onChange={(e) => setIgnorePipedriveDedup(e.target.checked)}
            />
            Ignore Pipedrive dedup (skip filtering out contacts already in
            Pipedrive)
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry run
          </label>
          <button onClick={onRun} disabled={mutation.isPending}>
            Rescore {count} selected
          </button>
          <StatusLine variant={variant} message={message} />
          <LogStream
            entries={logs.entries}
            isPolling={logs.isPolling}
            lastError={logs.lastError}
          />
          {mutation.isSuccess && <ContactResearchResult data={mutation.data} />}
          {mutation.isError && <JsonResult data={errorPayload(mutation.error)} />}
        </>
      )}
    </div>
  );
}
